/**
 * Created by rathompson on 1/19/22
 * File: Licenses
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { EmptyStateLayout, Tooltip, Button, LoadingIcon } from '@panwds/react-ui'
// import statement lib, local then scss or css
import { useCallback, useEffect, useMemo, useState } from 'react'
import { parseISO, format } from 'date-fns'
import FadedLineHeader from '../../../../../common/components/fadedLineHeader/FadedLineHeader'
import { DO_NOT_DISPLAY_ADDONS } from '../../../../../common/constants/LabelConstants'
import OnboardingCard from '../OnboardingCard'
import LicenseLineItem from '../../../LicenseCard/LicenseLineItem'
import {
  APP_NOTES,
  BUTTON_ACTIVATE,
  LABEL_EXPIRES,
  CDL_MIGRATION_MSG,
  SASE_SELECT_CDL,
} from '../../../../constants/LabelConstants'
import { FF_CDL_TSG_MIGRATION, FF_SEND_XSIG } from '../../../../../common/utils/FeatureFlags'
import ActivationForm from './ActivationForm'
import { ACTIVATION_READY } from '../../../../constants/LabelConstants'
import { ARRAY, getSignature, indexArray } from '../../../../../common/utils/Utils'
import SubscriptionService from '../../../../services/SubscriptionService'
import {
  CreateRootTSG,
  getUserCount,
  LoadInstances,
  LoadLicense,
  LoadTreeDataAsync,
  UpdateGlobalTsgID,
} from '../../../../../common/actions/GlobalActions'
import {
  ACTIVATE_TOOLTIP_NO_MSG,
  ADD_NEW,
  ENTERPRISE,
  TOOLTIP_ACTIVATE_BUTTON,
} from '../../../constants/SubscriptionConstant'
import { LOAD_TSG_ID, selectTreeNode } from '../../../../../common/reducers/actions/TreeReducer.actions'
import {
  APP_ID_PANORAMA,
  APP_ID_PRISMA_ACCESS,
  APP_ID_PRISMA_ACCESS_EDITION,
  APP_ID_PRISMA_ACCESS_PANORAMA,
  APP_ID_PRISMA_ACCESS_EDITION_ONPREM,
  APP_ID_LOGGING_SERVICE,
  APP_ID_DSS_ID,
  APP_ID_PRISMA_SAAS,
  APP_ID_SDWAN,
  APP_ID_DLP,
  APP_ID_IOT,
  APP_ID_FOR_TITLE,
  CLOUD_APPS,
  CTX_CDL,
  LICENSE_CONTEXT_TO_APP_ID,
  APP_ID_BACKBONE,
} from '../../../../../common/constants/GlobalConstants'
import { setActivateLoading } from '../../../../../common/reducers/actions/LicenseReducer.actions'
import { calculateNewTsg, getAppTitle } from '../../../../util/common'
import { getVarValue } from '../../../../../common/utils/Sparky'
import InstanceTitle from '../Instances/InstanceTitle'

const PLATFORMS = Object.freeze({
  ONPREM: 'onprem',
  CLOUD: 'cloud'
})

const ALLOW_USE_EXISTING_APPS = new Set([APP_ID_PRISMA_ACCESS_EDITION, APP_ID_PRISMA_ACCESS, APP_ID_PRISMA_ACCESS_EDITION_ONPREM, APP_ID_LOGGING_SERVICE, APP_ID_DSS_ID, APP_ID_DLP, APP_ID_IOT, APP_ID_PRISMA_SAAS])
const sendXsig = getVarValue(FF_SEND_XSIG, false)
const emptyInstances = indexArray([], 'app_id')

const Licenses = ({
  license,
  licenses,
  licenseDispatch,
  licenseContext,
  licenseHash,
  apps,
  globalDispatch,
  licenseInstances,
  dispatch,
  setWarning,
  setError,
  tenants,
  domainPostFix,
  isPanoramaLocked,
  selectedTreeNode,
  activateLoading,
  auth,
  globalTsgId,
  setPanorama,
  isAmend,
  errorDescription,
  ...rest
}) => {
  const { licenseMessage } = rest
  const [showForm, setShowForm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedTSGInstances, setSelectedTSGInstances] = useState(emptyInstances)
  const [validationReTrigger, setValidationReTrigger] = useState(false)
  const [isBackBoneInstanceAvailable, setBackBoneInstanceAvailable] = useState(false)
  useEffect(() => {
    if (activateLoading) {
      setShowForm(true)
    }
  }, [activateLoading])

  const primaryAppId = APP_ID_FOR_TITLE[licenseContext] || APP_ID_PRISMA_ACCESS

  const appDef = useMemo(() => {
    return apps[primaryAppId] || apps[LICENSE_CONTEXT_TO_APP_ID[primaryAppId]]
  }, [apps, primaryAppId])

  const onSubmit = useCallback(async ({
    is_eula_accepted,
    support_account,
    isDSSRequired = false,
    region_name: region,
    platform,
    tsg,
    deployment_size,
    description,
    ...body }) => {
    const isMigrateCDL = FF_CDL_TSG_MIGRATION && appDef?.setup?.extra?.migrate_cdl && body.hasOwnProperty(CTX_CDL) && body[CTX_CDL]?.migrate
    /**
     * create instances array based on licenses and selected TSG
     * SLS and addon can be existing inside selected TSG
     *
     * @return {Map} of instances
     */
    const generateActivationInstances = (licenseContext, licenses, primaryLicense = {}, apps, selectedTSGInstances, platform) => {
      const createInstance = ({ app_id, tenant_id = undefined, platform = PLATFORMS.CLOUD }) => {
        let _app_id = app_id
        if (platform === PLATFORMS.ONPREM && app_id === APP_ID_PRISMA_ACCESS) {
          _app_id = APP_ID_PRISMA_ACCESS_PANORAMA
        }
        if (platform === PLATFORMS.ONPREM && app_id === APP_ID_PRISMA_ACCESS_EDITION) {
          _app_id = APP_ID_PRISMA_ACCESS_EDITION_ONPREM
        }
        let _tenant_id = tenant_id ?? ((ALLOW_USE_EXISTING_APPS.has(_app_id) && selectedTSGInstances.has(_app_id)) ? selectedTSGInstances.get(_app_id).tenant_id : 'new')
        if (_app_id === APP_ID_LOGGING_SERVICE && isMigrateCDL) {
          _tenant_id = body[CTX_CDL]?.id
        }
        return {
          app_id: _app_id,
          tenant_id: _tenant_id,
        }
      }
      const instMap = new Map()
      // primary app
      if (!isAmend) {
        const appContext = licenseContext === CTX_CDL ? APP_ID_LOGGING_SERVICE : licenseContext
        instMap.set(appContext, createInstance({ app_id: appContext, platform }))
        if (licenseContext === APP_ID_PRISMA_ACCESS && apps[APP_ID_DSS_ID].enabled && isDSSRequired) {
          // always include CIE when license is PA
          instMap.set(APP_ID_DSS_ID, createInstance({ app_id: APP_ID_DSS_ID }))
        }
      }
      if (ARRAY.hasArray(licenses)) {
        for (const license of licenses) {
          if (!instMap.has(license.app_id)) {
            instMap.set(license.app_id, createInstance({ app_id: license.app_id, platform }))
          }
        }
        // manually adding SLS even not present in magic link
        if (licenseContext === APP_ID_PRISMA_ACCESS) {
          if (!instMap.has(APP_ID_LOGGING_SERVICE)) {
            instMap.set(APP_ID_LOGGING_SERVICE, createInstance({ app_id: APP_ID_LOGGING_SERVICE, platform }))
          }
        }
      }
      // building addon instances
      if (primaryLicense.entitlements) {
        for (const entitlement of primaryLicense.entitlements) {
          if (apps.hasOwnProperty(entitlement.app_id) && entitlement.asset_type === 'addon') {
            if (!instMap.has(entitlement.app_id)) {
              instMap.set(entitlement.app_id, createInstance({
                app_id: entitlement.app_id,
                tenant_id: entitlement.app_id === APP_ID_SDWAN ? entitlement.tenant_id : undefined
              }))
            }
          }
        }
      }

      instMap.delete('panorama')
      instMap.delete('support')
      return instMap
    }
    setValidationReTrigger(false)
    setError('')
    //dispatch(setActivateLoading(false))
    try {
      dispatch(setActivateLoading(true))
      if (tsg.id === ADD_NEW) {
        const display_name = calculateNewTsg(support_account.value, support_account.value, rest.tsgDropDownDetails)
        tsg = await CreateRootTSG({ display_name })
      }
      const payload = {
        hash: licenseHash,
        license_context: licenseContext,
        is_eula_accepted,
        support_account_id: String(support_account.id),
        support_account_name: support_account.value,
        region: region.id,
        tsg_name: tsg.value || tsg.display_name,
        platform: (platform || CLOUD_APPS.has(licenseContext)) ? PLATFORMS.CLOUD : PLATFORMS.ONPREM,
      }
      const activationInstMap = generateActivationInstances(licenseContext, licenses, license, apps, selectedTSGInstances, payload.platform)
      if (description) {
        if (activationInstMap.has(licenseContext)) {
          activationInstMap.get(licenseContext).description = description
        }
      }
      if (licenseContext === APP_ID_PRISMA_ACCESS && payload.platform === PLATFORMS.ONPREM && !isAmend) {
        const panoramaLicense = licenses.find(({ app_id }) => app_id === APP_ID_PANORAMA)
        const validateResponse = await SubscriptionService.validate.panorama({
          tsg_id: tsg.id,
          request: {
            serial_number: body.panorama_sn,
            support_account_id: String(support_account.id),
            register_new: !Boolean(body.panorama.id),
            has_support_license: Boolean(panoramaLicense?.auth_code),
            cdl_tenant_id: selectedTSGInstances?.get(APP_ID_LOGGING_SERVICE)?.tenant_id,
          }
        })
        const { isRegistered, hasSupport, hasCdlMapped } = validateResponse?.data
        const panoramaInst = activationInstMap.get(APP_ID_PRISMA_ACCESS)
        panoramaInst.extra = { isRegistered, hasSupport, hasCdlMapped }
        panoramaInst.serial_number = body.panorama_sn
        // panoramaInst.app_id = APP_ID_PRISMA_ACCESS_PANORAMA
        // activationInstMap.get(APP_ID_PRISMA_ACCESS_EDITION).app_id = APP_ID_PRISMA_ACCESS_EDITION_ONPREM
      }
      if (body.hasOwnProperty(APP_ID_PRISMA_SAAS)) {
        if (activationInstMap.has(APP_ID_PRISMA_SAAS)) {
          const apetureInst = activationInstMap.get(APP_ID_PRISMA_SAAS)
          apetureInst.subdomain = body[`${APP_ID_PRISMA_SAAS}_subnet`].toLowerCase()
          apetureInst.domainPostfix = domainPostFix[APP_ID_PRISMA_SAAS]
        }
      }
      if (body.hasOwnProperty(APP_ID_IOT)) {
        if (activationInstMap.has(APP_ID_IOT)) {
          const zingboxInst = activationInstMap.get(APP_ID_IOT)
          zingboxInst.subdomain = body[`${APP_ID_IOT}_subnet`].toLowerCase()
          zingboxInst.domainPostfix = domainPostFix[APP_ID_IOT]
        }
      }
      // check if SLS instance is here or migrating from CSP
      if (licenseContext === APP_ID_PRISMA_ACCESS && !isMigrateCDL) {
        const cdlLicense = licenses.find(each => each.app_id === APP_ID_LOGGING_SERVICE)
        if (!cdlLicense || cdlLicense.is_csp_activated) {
          if (activationInstMap.get(APP_ID_LOGGING_SERVICE)?.tenant_id === 'new') {
            throw new Error(`This activation doesn't have ${SASE_SELECT_CDL}, please make sure the activation link or selected Tenant has one`)
          }
        }
      }
      //Adding tenant_id for SLS migration
      if (isMigrateCDL) {
        payload.migrate_csp_instance = {
          tenant_ids: [body[CTX_CDL]?.id]
        }
        setWarning(CDL_MIGRATION_MSG)
      }
      payload.instances = Array.from(activationInstMap.values())
      payload.instances?.forEach((element) => {
        const _app_id = element.app_id === APP_ID_PRISMA_ACCESS_EDITION ? APP_ID_PRISMA_ACCESS : element.app_id
        if (body[`region_name:${_app_id}`]) {
          element.region = body[`region_name:${_app_id}`]?.id
        }
        if (_app_id === APP_ID_PRISMA_ACCESS && body?.isSpInterconnectRequired) {
          const _backboneRegion = body?.backboneRegion?.length ? body?.backboneRegion?.split(',') : []
          element.backbone_required = body?.isSpInterconnectRequired
          element.backbone_excluded_regions = _backboneRegion.map((each) => each?.trim().toLowerCase())
        }
      })
      // attach x-signature
      const signature = getSignature(support_account.id, auth?.user?.supportAccountIds)
      const notonorafter = auth?.user?.notonorafter
      const xsig = `${support_account.id} ${notonorafter} ${signature}`
      if (sendXsig) {
        payload.xsig = xsig
      }
      // dispatch(setActivateLoading(false))
      // return console.log('PAYLOAD', payload)
      const { data } = await SubscriptionService.activate.post({
        tsg_id: tsg.id,
        request: payload,
      })
      // console.log('PAYLOAD', payload)
      // console.log('BODY', body)
      // const data = null
      if (data) {
        dispatch(selectTreeNode(tsg))
        await globalDispatch.switchTsg(tsg?.id, { forceReload: false })
        await UpdateGlobalTsgID(licenseDispatch, tsg.id, LOAD_TSG_ID)
        await LoadTreeDataAsync(dispatch)
        LoadLicense({
          tsg_id: tsg.id, hash: licenseHash,
          flow_type: ENTERPRISE,
          license_context: licenseContext
        }, licenseDispatch)
        LoadInstances({
          tsg_id: tsg.id
        }, licenseDispatch)
        dispatch(setActivateLoading(false))
      }
    }
    catch (error) {
      setError(error.message ?? 'Error activating the License')
      dispatch(setActivateLoading(false))
      setValidationReTrigger(true)
    }
  }, [setError, setWarning, dispatch, licenseHash, licenseContext, licenses, license, apps, selectedTSGInstances, auth?.user?.supportAccountIds, auth?.user?.notonorafter, rest.tsgDropDownDetails, domainPostFix, globalDispatch, licenseDispatch, isAmend, appDef?.setup?.extra?.migrate_cdl])

  useEffect(() => {
    setBackBoneInstanceAvailable(!!selectedTSGInstances.get(APP_ID_BACKBONE))
    //setBackBoneInstanceAvailable(true)
  }, [selectedTSGInstances])
  const onActivate = useCallback(() => {
    setShowForm(!showForm)
  }, [showForm])

  const onCancel = useCallback(() => {
    setShowForm(false)
  }, [])

  const onTSGChange = useCallback(async (tsg_id) => {
    setLoading(true)
    try {
      if (tsg_id !== ADD_NEW) {
        const { data } = await SubscriptionService.instances.list({ tsg_id })
        setSelectedTSGInstances(indexArray(data, 'app_id'))
      }
      else {
        // when tsg selection is create new
        setSelectedTSGInstances(emptyInstances)
      }
    }
    catch (error) {
      setError(error.message)
    }
    setLoading(false)
  }, [setError])

  const productDetails = useMemo(() => {
    const expiry = license?.license_expiration ? format(parseISO(license?.license_expiration), 'MM/dd/yyyy') : null
    const addons = license?.entitlements?.filter(each => each.asset_type === 'addon' && !DO_NOT_DISPLAY_ADDONS.has(each.app_id))
      .reduce((prev, cur) => {
        // combine bundled add-ons to 1 item
        if (!cur.bundle_display_name) {
          prev.push(cur)
        }
        else if (!prev.some(each => each.license_display_name === cur.bundle_display_name)) {
          prev.push({ license_display_name: cur.bundle_display_name })
        }
        return prev
      }, [])
      .map(v => ({ ui_display_name: v.license_display_name }))
    const imageName = license?.app_id
    const purchasedSize = license?.entitlements?.find(each => each.app_id === primaryAppId)?.purchased_size
    const quantity = purchasedSize ? getUserCount(license.license_type, purchasedSize) : null
    const label = getAppTitle(license, apps[license?.app_id])
    const ions = license?.entitlements?.reduce((object, entitlement) => {
      if (entitlement.license_display_name === 'Physical IONs' || entitlement.license_display_name === 'Virtual IONs') {
        object[entitlement.license_display_name] = entitlement.purchased_size
      }
      return object
    }, {})
    const note = APP_NOTES[primaryAppId]
    return {
      imageName,
      label,
      expiry,
      addons,
      quantity,
      ions,
      note
    }
  }, [license, apps, primaryAppId])

  if (license) {
    return (<div>
      <FadedLineHeader title={ACTIVATION_READY} />
      <OnboardingCard appearance='onboarding'>
        {/* Right Controls */}
        <div className="tw-absolute tw-flex tw-items-center tw-gap-2 tw-right-3 tw-top-3">
          {(loading || activateLoading) && <LoadingIcon size="xxl" />}
          {license?.license_expiration && (
            <LicenseLineItem
              color="green"
              label={LABEL_EXPIRES}
              value={format(parseISO(license?.license_expiration), 'MM/dd/yyyy')} />
          )}
          {!showForm && (
            <Tooltip label={rest.userFlow.length ? TOOLTIP_ACTIVATE_BUTTON : ACTIVATE_TOOLTIP_NO_MSG}>
              <Button appearance="primary" onClick={onActivate}>{BUTTON_ACTIVATE}</Button>
            </Tooltip>
          )}
        </div>
        <InstanceTitle {...{ appDef, license }} />
        <ActivationForm
          {...{
            onSubmit,
            onCancel,
            showForm,
            license,
            licenses,
            licenseContext,
            setSelectedTSGInstances,
            loading: loading || activateLoading,
            productDetails,
            apps,
            onTSGChange,
            selectedTSGInstances,
            tenants,
            domainPostFix,
            setError,
            isPanoramaLocked,
            selectedTreeNode,
            setPanorama,
            isAmend,
            setLoading,
            validationReTrigger,
            setValidationReTrigger,
            globalTsgId,
            dispatch,
            isBackBoneInstanceAvailable,
            ...rest,
          }}
        />
      </OnboardingCard>
    </div>
    )
  }
  else if (licenseHash) {
    const description = errorDescription ?? licenseMessage
    return <>
      <FadedLineHeader title={ACTIVATION_READY} />
      <OnboardingCard appearance='onboarding'>
        <EmptyStateLayout description={description} />
      </OnboardingCard>
    </>
  }
  else {
    return <></>
  }
}

Licenses.propTypes = {}
export default Licenses
